import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import FactTiles from '../../components/student/CompetitionOverview/FactTiles';
import CompetitionTimeline from '../../components/student/CompetitionOverview/CompetitionTimeline';
import DeliverablesAccordion from '../../components/student/CompetitionOverview/DeliverablesAccordion';
import KeyRules from '../../components/student/CompetitionOverview/KeyRules';
import ImportantLinks from '../../components/student/CompetitionOverview/ImportantLinks';
import ApplicationProcess from '../../components/student/CompetitionOverview/ApplicationProcess';
import SubmissionRoadmap from '../../components/student/CompetitionOverview/SubmissionRoadmap';
import ApplicationCtaBanner from '../../components/student/CompetitionOverview/ApplicationCtaBanner';
import SectionBoundary from '../../components/student/CompetitionOverview/SectionBoundary';
import { getMyEnrollments } from '../../services/competitionWorkflowService';
import { getCompetitionOverview } from '../../services/competitionOverviewService';
import {
  buildCompetitionCards, parseDateOnly, formatDeadline, daysUntil, daysChip, isHttpUrl,
} from '../../utils/competitionCards';
import styles from './StudentCompetitionOverview.module.css';

/**
 * Competition overview: /student/competitions/:templateId
 *
 * Where the content comes from:
 *  - The student's enrollments: the tab row, the page name fallback, the
 *    Start/Continue rule and the deadline fallback.
 *  - getCompetitionOverview(): everything else, parsed from the competition's
 *    guide that an admin uploaded on the Competition Setup page. See
 *    services/competitionOverviewService.js for the data shape.
 *
 * Below the header, in order: fact tiles, timeline, deliverables, rules, links,
 * process, roadmap and the closing banner (all in
 * components/student/CompetitionOverview). Each hides itself when the overview
 * has no data for it.
 *
 * A competition whose guide has not been uploaded yet (`published: false`) gets
 * a "details coming soon" notice instead of that stack — with the application
 * process and the Start/Continue button kept, because the workspace is open
 * either way and does not depend on this page.
 */

const StudentCompetitionOverview = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState(null); // null until loaded
  const [enrollmentsFailed, setEnrollmentsFailed] = useState(false);
  const [overview, setOverview] = useState(null);
  const [overviewFailed, setOverviewFailed] = useState(false);

  const loadEnrollments = useCallback(async () => {
    setEnrollmentsFailed(false);
    try {
      setEnrollments(await getMyEnrollments());
    } catch {
      setEnrollmentsFailed(true);
    }
  }, []);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  // The overview is fetched per competition, so switching tabs reloads only it.
  const loadOverview = useCallback(async (id, isCurrent = () => true) => {
    setOverview(null);
    setOverviewFailed(false);
    try {
      const data = await getCompetitionOverview(id);
      if (isCurrent()) setOverview(data);
    } catch {
      if (isCurrent()) setOverviewFailed(true);
    }
  }, []);

  useEffect(() => {
    let current = true;
    loadOverview(templateId, () => current);
    return () => { current = false; };
  }, [templateId, loadOverview]);

  const cards = useMemo(() => buildCompetitionCards(enrollments).cards, [enrollments]);
  const current = cards.find((c) => c.templateId === templateId) || null;

  if (enrollmentsFailed || overviewFailed) {
    return (
      <div className={styles.container}>
        <Card>
          <div className={styles.empty}>
            <p>Failed to load this competition.</p>
            <Button
              size="small"
              variant="outline"
              onClick={() => {
                if (enrollmentsFailed) loadEnrollments();
                if (overviewFailed) loadOverview(templateId);
              }}
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (enrollments === null || (current && !overview)) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading competition..." />
      </div>
    );
  }

  // Not enrolled (a withdrawn enrollment is hidden by the API as well), or an
  // id that is not a competition at all.
  if (!current) {
    return (
      <div className={styles.container}>
        <Card>
          <div className={styles.empty}>
            <p>This competition could not be found, or you are not enrolled in it.</p>
            <Link to="/student/competitions" className={styles.backLink}>
              ← Back to My Competitions
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const title = overview.name || current.name;
  const stage = (overview.stages || []).find((s) => s.key === overview.currentStageKey) || null;

  // The deadline box prefers the overview content. When that has none (an
  // entry without one, or a competition with no overview yet), it falls back to
  // the real official deadline from the enrollment, with no sample-data tag.
  const od = overview.nextOfficialDeadline;
  const odDate = od ? parseDateOnly(od.date) : current.date;
  const showDeadlineBox = Boolean(od || current.date);
  const today = new Date();
  const odChip = odDate
    ? daysChip(daysUntil(odDate, new Date(today.getFullYear(), today.getMonth(), today.getDate())))
    : null;

  const started = current.status !== 'not_started';
  const applicationLabel = started ? 'Continue My Application' : 'Start My Application';
  const applicationPath = `/student/competitions/${templateId}/application`;
  const website = overview.officialWebsite;

  return (
    <div className={styles.container}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/student/competitions">My Competitions</Link>
        <span aria-hidden="true"> › </span>
        <span>{title}</span>
      </nav>

      {cards.length > 1 && (
        <nav className={styles.tabs} aria-label="Your competitions">
          <span className={styles.tabsLabel}>Your competitions</span>
          {cards.map((c) => (
            <Link
              key={c.templateId}
              to={`/student/competitions/${c.templateId}`}
              className={`${styles.tab} ${c.templateId === templateId ? styles.tabActive : ''}`}
              aria-current={c.templateId === templateId ? 'page' : undefined}
            >
              {c.name}
            </Link>
          ))}
        </nav>
      )}

      <Card className={styles.header}>
        <div className={styles.headerMain}>
          <h1>{title}</h1>
          {overview.tagline && <p className={styles.tagline}>{overview.tagline}</p>}
          <div className={styles.headerChips}>
            {stage && (
              <span className={styles.stageChip}>You are preparing: {stage.name}</span>
            )}
            {website && isHttpUrl(website.url) && (
              <a
                className={styles.websiteLink}
                href={website.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {website.label || 'Visit Official Competition Website'} ↗
              </a>
            )}
          </div>
        </div>

        {showDeadlineBox && (
          <div className={styles.deadlineBox}>
            <span className={styles.eyebrow}>Next official deadline</span>
            <div className={styles.deadlineDate}>
              {odDate ? formatDeadline(odDate) : 'Deadline not yet added'}
              {od?.isPlaceholder && odDate && <span className={styles.sampleTag}>Sample date</span>}
            </div>
            {od?.stageLabel && <div className={styles.deadlineStage}>{od.stageLabel}</div>}
            {odChip && (
              <span className={`${styles.chip} ${styles[odChip.tone]}`}>{odChip.text}</span>
            )}
          </div>
        )}
      </Card>

      <div className={styles.actions}>
        <Link to="/student/competitions" className={styles.backLink}>
          ← Back to My Competitions
        </Link>
        <Button size="small" onClick={() => navigate(applicationPath)}>
          {applicationLabel} →
        </Button>
      </div>

      {/* Keyed by competition so every section (and any boundary that caught an
          error) starts fresh when the student moves to another competition. */}
      <div key={templateId} className={styles.sections}>
        {!overview.published && (
          <Card className={styles.pending}>
            <h2 className={styles.pendingTitle}>Competition details coming soon</h2>
            <p>
              The stages, deadlines and requirements for {title} have not been published
              yet. They appear here as soon as your CreatED team adds the competition's
              official guide.
            </p>
            <p className={styles.pendingNote}>
              You can start your application now — your workspace does not wait for this
              page. Check the official competition website for anything you need before
              then.
            </p>
          </Card>
        )}
        <SectionBoundary name="facts">
          <FactTiles overview={overview} currentStage={stage} />
        </SectionBoundary>
        <SectionBoundary name="timeline">
          <CompetitionTimeline stages={overview.stages} currentStageKey={overview.currentStageKey} />
        </SectionBoundary>
        <SectionBoundary name="deliverables">
          <DeliverablesAccordion stages={overview.stages} currentStageKey={overview.currentStageKey} />
        </SectionBoundary>
        <SectionBoundary name="rules">
          <KeyRules rules={overview.rules} />
        </SectionBoundary>
        <SectionBoundary name="links">
          <ImportantLinks links={overview.links} />
        </SectionBoundary>
        <SectionBoundary name="process">
          <ApplicationProcess steps={overview.process} />
        </SectionBoundary>
        <SectionBoundary name="roadmap">
          <SubmissionRoadmap roadmap={overview.roadmap} />
        </SectionBoundary>
        <ApplicationCtaBanner
          stageName={stage?.name}
          label={applicationLabel}
          onStart={() => navigate(applicationPath)}
        />
      </div>
    </div>
  );
};

export default StudentCompetitionOverview;
